import { Link, useParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import FavoriteButton from "@/components/base/FavoriteButton";
import { movies } from "@/mocks/movies";
import { getRelatedMovies } from "@/utils/movieUtils";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find((item) => item.id === id);

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
          <i className="ri-movie-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">影片不存在</h1>
        <p className="text-sm text-foreground-600">你访问的电影档案不存在或已下架，请返回首页重新浏览。</p>
        <Link
          to="/"
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMovies(movies, movie, 4);

  const meta = [
    { label: "导演", value: movie.director },
    { label: "主演", value: movie.cast.join("、") },
    { label: "年份", value: `${movie.year} 年` },
    { label: "类型", value: movie.genre.join(" / ") },
    { label: "片长", value: `${movie.runtime} 分钟` },
    { label: "地区", value: movie.region },
  ];

  return (
    <article className="w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <Link to={`/category/${movie.decade}`} className="cursor-pointer transition-colors hover:text-primary-600">分类</Link>
        <i className="ri-arrow-right-s-line text-foreground-300" />
        <span className="text-foreground-800">{movie.title}</span>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-300/70 bg-background-200">
            <img
              src={movie.cover}
              alt={`${movie.title} 海报`}
              title={`${movie.title} 香港三级片电影海报`}
              className="h-full w-full object-top"
            />
          </div>
          <div className="mt-4 flex items-center justify-between rounded-lg border border-background-300/70 bg-background-50 px-4 py-3">
            <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
              <i className="ri-star-fill text-accent-500" />
              影迷评分
            </span>
            <span className="font-heading text-xl font-bold text-primary-600">{movie.rating.toFixed(1)}</span>
          </div>
        </div>

        <div className="flex flex-col lg:col-span-2">
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
            <i className="ri-movie-2-line" />
            {movie.decade} · 电影档案
          </span>
          <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
            {movie.title}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-500">{movie.titleEn}</p>

          <div className="mt-6 grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-background-300/70 bg-background-300/70 sm:grid-cols-2">
            {meta.map((item) => (
              <div key={item.label} className="bg-background-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-foreground-400">{item.label}</p>
                <p className="mt-1 text-sm font-medium text-foreground-900">{item.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <FavoriteButton movieId={movie.id} movieTitle={movie.title} />
            <span className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
              <i className="ri-information-line" />
              本站仅提供资料展示，不包含播放或下载功能
            </span>
          </div>

          <div className="mt-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-500" />
              剧情简介
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.synopsis}</p>
            <p className="mt-3 text-sm leading-7 text-foreground-700">{movie.plot}</p>
          </div>

          <div className="mt-8">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-price-tag-3-line text-primary-500" />
              相关标签
            </h2>
            <div className="mt-3 flex flex-wrap gap-2">
              {[...movie.genre, ...movie.tags].map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section className="mt-14">
        <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
          <i className="ri-movie-line text-primary-500" />
          相关影片推荐
        </h2>
        <p className="mt-1 text-sm text-foreground-600">依据导演、类型与标签相关性为你推荐</p>
        {related.length === 0 ? (
          <p className="mt-5 rounded-lg border border-background-300/70 bg-background-50 p-8 text-center text-sm text-foreground-500">
            暂无相关推荐影片
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((item, index) => (
              <MovieCard key={item.id} movie={item} index={index} />
            ))}
          </div>
        )}
      </section>
    </article>
  );
}